/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */

export function cubicBezier(
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number
): (t: number) => number {
  const cx = 3 * p1x
  const bx = 3 * (p2x - p1x) - cx
  const ax = 1 - cx - bx
  const cy = 3 * p1y
  const by = 3 * (p2y - p1y) - cy
  const ay = 1 - cy - by

  function bezier(t: number, a: number, b: number, c: number) {
    return ((a * t + b) * t + c) * t
  }

  function bezierDerivative(t: number, a: number, b: number, c: number) {
    return (3 * a * t + 2 * b) * t + c
  }

  function getTForX(x: number) {
    let t = x
    for (let i = 0; i < 4; i++) {
      const xEst = bezier(t, ax, bx, cx) - x
      const dEst = bezierDerivative(t, ax, bx, cx)
      t -= dEst === 0 ? 0 : xEst / dEst
    }
    return t
  }

  return function (t: number) {
    const tForX = getTForX(t)
    return bezier(tForX, ay, by, cy)
  }
}

export function linearEasing(t: number): number {
  return t
}

function _calcValues(from: number[], to: number[], easedProgress: number) {
  if (easedProgress === 0) return [...from]
  return from.map((f, index: number) => {
    const t = to[index]
    if (t === f) return t
    return f + easedProgress * (t - f)
  })
}

function _direct(from: number[], to: number[], duration: number, opts: any) {
  const easingFunction = opts.easingFunction || linearEasing
  const scheduler = opts.scheduler
  const onProgress = opts.onProgress
  const onComplete = opts.onComplete
  const onBreak = opts.onBreak

  const startTime = Date.now()
  let elapsedTime = 0
  let currentValues = from
  let isBroken = false

  function step() {
    if (isBroken) {
      if (typeof onBreak === 'function') {
        onBreak({
          elapsedTime: elapsedTime,
          currentValue: currentValues[0],
          currentValues: currentValues
        })
      }
      return
    }

    elapsedTime = Date.now() - startTime
    const progress = Math.min(elapsedTime / duration, 1)
    const easedProgress = easingFunction(progress)
    currentValues = _calcValues(from, to, easedProgress)

    onProgress(
      {
        elapsedTime: elapsedTime,
        currentValue: currentValues[0],
        currentValues: currentValues
      },
      function () {
        isBroken = true
      }
    )

    if (progress < 1) {
      scheduler(step)
    } else if (typeof onComplete === 'function') {
      onComplete({
        elapsedTime: elapsedTime,
        currentValue: to[0],
        currentValues: to
      })
    }
  }

  scheduler(step)
}

export function directMulti(opts: any): any {
  let from, to
  if (
    typeof opts === 'object' &&
    typeof opts.from === 'object' &&
    typeof opts.from.length === 'number' &&
    opts.from.length > 1 &&
    typeof opts.to === 'object' &&
    typeof opts.to.length === 'number' &&
    opts.to.length === opts.from.length
  ) {
    from = opts.from
    to = opts.to
  } else {
    return console.log('[ ERROR ]', 'Invalid option for "from" or "to"')
  }

  if (typeof opts.duration !== 'number') {
    return console.log('[ ERROR ]', 'Duration must be a number')
  }
  const duration = opts.duration

  if (typeof opts.scheduler !== 'function') {
    return console.log('[ ERROR ]', 'scheduler is not a function')
  }

  if (typeof opts.onProgress !== 'function') {
    return console.log('[ ERROR ]', 'onProgress is not a function')
  }

  _direct(from, to, duration, opts)
}

export function direct(rangeOrTo: any, duration: number, opts: any) {
  let from, to
  if (
    typeof rangeOrTo === 'object' &&
    typeof rangeOrTo.length === 'number' &&
    rangeOrTo.length > 1
  ) {
    from = rangeOrTo[0]
    to = rangeOrTo[1]
  } else if (typeof rangeOrTo === 'number') {
    from = 0
    to = rangeOrTo
  } else {
    return console.log(
      '[ ERROR ]',
      'Invalid argument for "to" or "from-to" array'
    )
  }

  if (typeof duration !== 'number') {
    return console.log('[ ERROR ]', 'Duration must be a number')
  }

  if (typeof opts.scheduler !== 'function') {
    return console.log('[ ERROR ]', 'scheduler is not a function')
  }

  if (typeof opts.onProgress !== 'function') {
    return console.log('[ ERROR ]', 'onProgress is not a function')
  }

  _direct([from], [to], duration, opts)
}
